import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Activity, 
  Search, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  User,
  CheckCircle,
  XCircle,
  LogIn,
  FileText,
  Eye,
  MessageSquare,
  Download,
  Upload,
  Calendar,
  Filter,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  login_request: LogIn,
  login_success: CheckCircle,
  login_failed: XCircle,
  view_issues: Eye,
  view_issue_detail: FileText,
  create_issue: FileText,
  add_note: MessageSquare,
  download_attachment: Download,
  upload_attachment: Upload
};

const ACTION_COLORS = {
  login_request: 'text-blue-400',
  login_success: 'text-green-400',
  login_failed: 'text-red-400',
  view_issues: 'text-gray-400',
  view_issue_detail: 'text-purple-400',
  create_issue: 'text-emerald-400',
  add_note: 'text-yellow-400',
  download_attachment: 'text-cyan-400',
  upload_attachment: 'text-orange-400'
};

function CustomerActivityLogs({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;
  
  // Filters
  const [filters, setFilters] = useState({
    email: '',
    action: '',
    dateFrom: '',
    dateTo: '',
    successOnly: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    fetchActionTypes();
    fetchStats();
    fetchLogs();
  }, [token]);

  useEffect(() => {
    fetchLogs();
  }, [currentPage]);

  const fetchActionTypes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/customer-logs/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActionTypes(response.data.actions || []);
    } catch (error) {
      console.error('Error fetching action types:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/customer-logs/stats?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLogs = async (page = currentPage) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (filters.email) params.append('email', filters.email);
      if (filters.action) params.append('action', filters.action);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.successOnly !== null) params.append('success_only', filters.successOnly.toString());
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/customer-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
      setCurrentPage(page);
    } catch (error) {
      toast.error('Errore nel caricamento dei log');
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleFilter = () => {
    setCurrentPage(1);
    fetchLogs(1);
  };

  const handleClearFilters = () => {
    setFilters({
      email: '',
      action: '',
      dateFrom: '',
      dateTo: '',
      successOnly: null
    });
    setCurrentPage(1);
    fetchLogs(1);
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    const actionType = actionTypes.find(a => a.value === action);
    return actionType ? actionType.label : action;
  };

  const getActionIcon = (action) => {
    const IconComponent = ACTION_ICONS[action] || Activity;
    const colorClass = ACTION_COLORS[action] || 'text-gray-400';
    return <IconComponent className={`w-4 h-4 ${colorClass}`} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-activity-logs">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold font-mono mb-2" data-testid="logs-title">
            <Activity className="inline-block w-8 h-8 mr-2 text-blue-500" />
            Activity Log Clienti
          </h1>
          <p className="text-[rgb(161,161,170)] text-sm font-mono">
            Tracciamento accessi e operazioni del portale clienti
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowStats(!showStats)}
            className="font-mono"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            {showStats ? 'Nascondi Stats' : 'Mostra Stats'}
          </Button>
          <Button
            variant="outline"
            onClick={() => fetchLogs()}
            disabled={refreshing}
            className="font-mono"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-[rgb(39,39,42)] rounded-lg border border-[rgb(63,63,70)]">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-400">{stats.unique_users}</p>
            <p className="text-sm text-[rgb(161,161,170)] font-mono">Utenti Unici (30gg)</p>
          </div>
          {stats.action_stats?.slice(0, 3).map((stat, idx) => (
            <div key={idx} className="text-center">
              <p className="text-3xl font-bold text-emerald-400">{stat.count}</p>
              <p className="text-sm text-[rgb(161,161,170)] font-mono">{getActionLabel(stat._id)}</p>
              <p className="text-xs text-green-400">{stat.success_count} ok</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-[rgb(39,39,42)] rounded-lg border border-[rgb(63,63,70)] p-4">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => setShowFilters(!showFilters)}
            className="font-mono text-sm"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
          </Button>
          <span className="text-sm text-[rgb(161,161,170)] font-mono">
            {total} record trovati
          </span>
        </div>
        
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Email</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[rgb(161,161,170)]" />
                <Input
                  value={filters.email}
                  onChange={(e) => setFilters({...filters, email: e.target.value})}
                  placeholder="Cerca email..."
                  className="pl-10 bg-[rgb(24,24,27)] border-[rgb(63,63,70)] font-mono"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Azione</label>
              <Select
                value={filters.action}
                onValueChange={(value) => setFilters({...filters, action: value === 'all' ? '' : value})}
              >
                <SelectTrigger className="bg-[rgb(24,24,27)] border-[rgb(63,63,70)] font-mono">
                  <SelectValue placeholder="Tutte le azioni" />
                </SelectTrigger>
                <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                  <SelectItem value="all" className="font-mono">Tutte le azioni</SelectItem>
                  {actionTypes.map(action => (
                    <SelectItem key={action.value} value={action.value} className="font-mono">
                      {action.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Data Da</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                className="bg-[rgb(24,24,27)] border-[rgb(63,63,70)] font-mono"
              />
            </div>
            
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">Data A</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                className="bg-[rgb(24,24,27)] border-[rgb(63,63,70)] font-mono"
              />
            </div>
            
            <div className="flex items-end gap-2">
              <Button onClick={handleFilter} className="flex-1 font-mono">
                <Search className="w-4 h-4 mr-2" />
                Filtra
              </Button>
              <Button variant="outline" onClick={handleClearFilters} className="font-mono">
                Reset
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="bg-[rgb(39,39,42)] rounded-lg border border-[rgb(63,63,70)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[rgb(24,24,27)] border-b border-[rgb(63,63,70)]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Data/Ora</th>
                <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Utente</th>
                <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Azione</th>
                <th className="px-4 py-3 text-left text-xs font-mono text-[rgb(161,161,170)] uppercase">Dettagli</th>
                <th className="px-4 py-3 text-center text-xs font-mono text-[rgb(161,161,170)] uppercase">Stato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(63,63,70)]">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[rgb(161,161,170)] font-mono">
                    Nessun log trovato
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => (
                  <tr key={log.id || idx} className="hover:bg-[rgb(24,24,27)] transition-colors">
                    <td className="px-4 py-3 font-mono text-sm whitespace-nowrap">
                      <Calendar className="inline-block w-3 h-3 mr-1 text-[rgb(161,161,170)]" />
                      {formatDate(log.timestamp)}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">
                      <User className="inline-block w-3 h-3 mr-1 text-blue-400" />
                      {log.email}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-[rgb(161,161,170)]">
                      {log.mantis_username || '-'}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">
                      <span className="flex items-center gap-2">
                        {getActionIcon(log.action)}
                        {getActionLabel(log.action)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[rgb(161,161,170)] max-w-xs truncate">
                      {log.details && Object.keys(log.details).length > 0 ? (
                        <span title={JSON.stringify(log.details, null, 2)}>
                          {log.details.issue_id && `Issue #${log.details.issue_id}`}
                          {log.details.project && ` (${log.details.project})`}
                          {log.details.summary && ` - ${log.details.summary.substring(0, 30)}...`}
                          {log.details.filename && `📎 ${log.details.filename}`}
                        </span>
                      ) : (
                        log.error_message ? (
                          <span className="text-red-400" title={log.error_message}>
                            ⚠️ {log.error_message.substring(0, 50)}...
                          </span>
                        ) : '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.success ? (
                        <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[rgb(63,63,70)]">
            <div className="text-sm text-[rgb(161,161,170)] font-mono">
              Pagina {currentPage} di {totalPages} ({total} totali)
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(currentPage - 1)}
                disabled={currentPage <= 1 || refreshing}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(currentPage + 1)}
                disabled={currentPage >= totalPages || refreshing}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerActivityLogs;
